import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { validateInvitationToken, acceptInvitationWithPassword } from '../../services/invitationService';
import { InvitationValidationResponse } from '../../types';
import { Loader2, CheckCircle2, XCircle, Mail, Lock, Eye, EyeOff } from 'lucide-react';

const AcceptInvitePage: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validation, setValidation] = useState<InvitationValidationResponse | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidation({
          valid: false,
          message: t('invitations.acceptInvite.invalidToken')
        });
        setLoading(false);
        return;
      }

      try {
        const result = await validateInvitationToken(token);
        setValidation(result);
      } catch (error) {
        console.error('Error validating token:', error);
        setValidation({
          valid: false,
          message: t('invitations.acceptInvite.invalidToken')
        });
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token, t]);

  const handleAcceptInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !validation?.email) return;

    // Validate passwords
    if (password.length < 8) {
      setError(t('invitations.acceptInvite.passwordTooShort', 'Le mot de passe doit contenir au moins 8 caractères'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('invitations.acceptInvite.passwordMismatch', 'Les mots de passe ne correspondent pas'));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Accept invitation and create user with password
      const result = await acceptInvitationWithPassword(token, password);

      if (result.success) {
        // Show success message and redirect to welcome
        navigate('/welcome', {
          state: {
            message: t('invitations.acceptInvite.success', 'Compte créé avec succès! Bienvenue dans l\'organisation.')
          }
        });
      }
    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      const errorMessage = err.response?.data?.detail || t('invitations.acceptInvite.error', 'Une erreur est survenue');

      // If account already exists, suggest login
      if (errorMessage.includes('existe déjà') || errorMessage.includes('already exists')) {
        setError(`${errorMessage} ${t('invitations.acceptInvite.alreadyHaveAccount')}`);
      } else {
        setError(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t('invitations.acceptInvite.loading')}
          </h2>
        </div>
      </div>
    );
  }

  if (!validation?.valid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('invitations.acceptInvite.invalidToken')}
            </h1>
            <p className="text-gray-600">
              {validation?.message || t('invitations.acceptInvite.invalidMessage')}
            </p>
          </div>

          <button
            onClick={() => navigate('/')}
            className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
          >
            {t('global.backToHome', 'Retour à l\'accueil')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
            <Mail className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('invitations.acceptInvite.title')}
          </h1>
          <p className="text-gray-600">
            {t('invitations.acceptInvite.welcome')}
          </p>
        </div>

        <div className="bg-indigo-50 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-center mb-3">
            <CheckCircle2 className="w-5 h-5 text-indigo-600 mr-2" />
            <span className="text-sm font-medium text-indigo-900">
              {t('global.status')}: {t('global.pending')}
            </span>
          </div>

          <h2 className="text-xl font-bold text-indigo-900 text-center mb-2">
            {validation.organization_name}
          </h2>

          <div className="text-sm text-indigo-700 text-center space-y-1">
            <p>
              <span className="font-medium">{t('global.email')}:</span>{' '}
              {validation.email}
            </p>
            {validation.expires_at && (
              <p>
                <span className="font-medium">{t('invitations.acceptInvite.expiresIn')}:</span>{' '}
                {new Date(validation.expires_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>

        <form onSubmit={handleAcceptInvitation} className="space-y-4">
          {/* Password field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              {t('invitations.acceptInvite.password', 'Mot de passe')}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder={t('invitations.acceptInvite.passwordPlaceholder', 'Minimum 8 caractères')}
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label={showPassword ? t('invitations.acceptInvite.hidePassword') : t('invitations.acceptInvite.showPassword')}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Confirm password field */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
              {t('invitations.acceptInvite.confirmPassword', 'Confirmer le mot de passe')}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder={t('invitations.acceptInvite.confirmPasswordPlaceholder', 'Confirmer votre mot de passe')}
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label={showConfirmPassword ? t('invitations.acceptInvite.hidePassword') : t('invitations.acceptInvite.showPassword')}
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center group"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {t('invitations.acceptInvite.creating', 'Création du compte...')}
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                {t('invitations.acceptInvite.createAccount', 'Créer mon compte')}
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => navigate('/')}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-lg transition-colors"
          >
            {t('global.cancel', 'Annuler')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
